import React from "react";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { MinimalTableCell, MinimalTableCellWithTooltip } from "src/ui/shared/table/MinimalTableComponents";
import { tagsFilter as tagsFilterUtils, uploaderFilter } from "src/ui/shared/table/Tanstack.utils";

import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { createRolloutTag, getTagReactKey, sortTags } from "@dashboard/shared/tags";
import { createVersionCodeTag, FileInfoWithVersions, hasApkMetadata } from "@files/utils";
import { Box } from "@mui/material";
import UserSettingsService from "@services/userSettingsService";
import { IndicatorIcon } from "@shared/IndicatorIcon";
import { ColumnDef } from "@tanstack/react-table";

import ActionsCell from "./ActionsCell";
import { LicenseFileTabParams } from "./FileTab";
import { useStyles } from "./FileTab.style";

export const FileNameCell = ({ file }: { file: API.File }) => {
    const title = hasApkMetadata(file) ? file.metadata?.file?.apk?.manifest?.package : file.filename;
    return <MinimalTableCellWithTooltip title={title || "-"} style={{ maxWidth: 260 }} />;
};

export const SourceCell = ({ file }: { file: API.File }) => (
    <MinimalTableCell>
        <Box>
            {file.source === 0 ? (
                <IndicatorIcon icon="fa-solid fa-user" tooltip={i18next.t("sidepanels:devicePanel.tooltip.user")} />
            ) : null}
            {file.source === 1 ? (
                <IndicatorIcon icon="fa-solid fa-microchip" tooltip={i18next.t("sidepanels:devicePanel.tooltip.device")} />
            ) : null}
        </Box>
    </MinimalTableCell>
);

export const FileTabTagsCell = ({ file }: { file: API.File }) => {
    const { classes } = useStyles();
    const versionCodeTag = createVersionCodeTag(file);
    const rolloutTag = createRolloutTag(file.rolloutPercentage);
    const removableTags = sortTags(file.metadata?.user?.tags ?? []);
    const generatedTags = sortTags([versionCodeTag, rolloutTag].filter((tag) => tag !== null));

    return (
        <MinimalTableCell>
            <div className={classes.tagsBox}>
                {removableTags.map((item) => (
                    <Tag key={getTagReactKey(item)} item={item} />
                ))}
                {generatedTags.map((item) => (
                    <Tag key={getTagReactKey(item)} item={item} generated />
                ))}
            </div>
        </MinimalTableCell>
    );
};

export const columns: ColumnDef<FileInfoWithVersions, unknown>[] = [
    {
        id: "collapseButton",
        header: "",
        enableSorting: false,
        cell: () => null,
    },
    {
        header: i18next.t("common:label.packageName"),
        meta: { headerTooltip: i18next.t("common:tooltip.packageNameHeader") },
        enableSorting: true,
        accessorKey: "filename",
        cell: (info) => <FileNameCell file={info.row.original} />,
    },
    {
        header: i18next.t("common:label.uploader"),
        accessorKey: "source",
        enableSorting: true,
        filterFn: uploaderFilter,
        cell: (info) => <SourceCell file={info.row.original} />,
    },
    {
        header: i18next.t("common:label.tags"),
        accessorKey: "tags",
        enableSorting: false,
        meta: { filterVariant: "filesTags" },
        filterFn: tagsFilterUtils,
        cell: (info) => <FileTabTagsCell file={info.row.original} />,
    },
    {
        accessorKey: "action",
        header: "",
        enableSorting: false,
        cell: (info) => (
            <MinimalTableCell style={{ overflow: "visible" }}>
                <ActionsCell file={info.row.original} />
            </MinimalTableCell>
        ),
    },
];

export function useParams(groupId: number): [LicenseFileTabParams, React.Dispatch<React.SetStateAction<LicenseFileTabParams>>] {
    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const [storedParams, setParams] = React.useState<LicenseFileTabParams>(() => {
        const base = UserSettingsService.getLicenseSidePanelFileTabParams();
        base.groups = [groupId];
        if (workspaceId !== null) {
            base.workspaceId = workspaceId;
        }
        return base;
    });

    // The panel is reused across devices rather than remounted, so the group and workspace it is looking at are the
    // caller's to decide. Without merging them in, selecting a device in another group keeps querying the group the
    // panel first opened with, while the header and the row actions already show the new one.
    const params = React.useMemo(
        () => ({
            ...storedParams,
            groups: [groupId],
            ...(workspaceId !== null && { workspaceId }),
        }),
        [storedParams, groupId, workspaceId],
    );

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("licenseSidePanel.fileTab.params", params);
    }, [params]);

    return [params, setParams];
}
